import React, { useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, CircleMarker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { TopBarMenu } from "../components/TopBarMenu";
import { TopLeftComponent } from "../components/TopLeftComponent";
import { useAskParrotsMutation } from "../slices/AiSlice";
import { PulsatingParrotLogo } from "../components/PulsatingParrotLogo";
import { invokeHub } from "../signalr/signalRHub";
import { useSelector } from "react-redux";
import {
  parrotBoatPurple, parrotCarRed, parrotCaravanOrangeRed, parrotBusYellowGreen,
  parrotWalkTurquoise, parrotRunLightOrange, parrotMotorcycleDarkRed,
  parrotBicycleTealGreen, parrotTinyHouseLightYellow, parrotAirplaneLightGreen,
  parrotTrainPink, parrotBlue, parrotPlaceholderGrey,
} from "../styles/colors";

const VEHICLES = ["Boat", "Car", "Caravan", "Bus", "Walk", "Run", "Motorcycle", "Bicycle", "TinyHouse", "Airplane", "Train"];
const DURATIONS = ["Half day", "1 day", "2-3 days", "1 week", "2 weeks"];
const VIBES = ["Culture", "Food", "Nature", "Chill", "Adventure", "Budget", "Scenic", "Any"];
const RADII = ["1km", "5km", "10km", "50km"];
const ON_FOOT = ["Walk", "Run"];

const VEHICLE_COLORS = {
  Boat: parrotBoatPurple, Car: parrotCarRed, Caravan: parrotCaravanOrangeRed,
  Bus: parrotBusYellowGreen, Walk: parrotWalkTurquoise, Run: parrotRunLightOrange,
  Motorcycle: parrotMotorcycleDarkRed, Bicycle: parrotBicycleTealGreen,
  TinyHouse: parrotTinyHouseLightYellow, Airplane: parrotAirplaneLightGreen, Train: parrotTrainPink,
};

const VIBE_COLORS = {
  Culture: "#8B5CF6", Food: "#F59E0B", Nature: "#10B981", Chill: "#06B6D4",
  Adventure: "#EF4444", Budget: "#6B7280", Scenic: "#3B82F6", Any: "#9CA3AF",
};

const RADIUS_COLORS = {
  "1km": parrotBlue, "5km": "#0EA5E9", "10km": "#6366F1", "50km": "#8B5CF6",
};

const DURATION_COLORS = {
  "Half day": "#10B981", "1 day": "#0EA5E9", "2-3 days": "#6366F1",
  "1 week": "#8B5CF6", "2 weeks": "#EC4899",
};

const VIBE_DESCRIPTIONS = {
  Culture: "Culture (focused on cultural sights and history)",
  Food: "Food (focused on local food and dining)",
  Nature: "Nature (focused on nature and outdoor scenery)",
  Chill: "Chill (relaxed and laid-back)",
  Adventure: "Adventure (adventurous and off the beaten path)",
  Budget: "Budget (budget-friendly)",
  Scenic: "Scenic (focused on scenic landscapes and views)",
};

const purpleIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function MapClickHandler({ onMapClick }) {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
}

function MapCenterSetter({ center }) {
  const map = useMap();
  React.useEffect(() => { map.setView(center); }, [center, map]);
  return null;
}

function formatDuration(d) { return d === "Half day" ? "Half a day" : d; }

function buildQueryText(vehicle, duration, vibe, radius, pin) {
  const isOnFoot = ON_FOOT.includes(vehicle);
  const displayDuration = formatDuration(duration);
  const vehiclePart = isOnFoot
    ? `I want to go for a ${vehicle} for ${displayDuration}.`
    : `I have a ${vehicle} and ${displayDuration} available.`;
  const vibePart = vibe === "Any"
    ? "I'm looking for a voyage of any vibe"
    : `I'm looking for a ${VIBE_DESCRIPTIONS[vibe] ?? vibe} experience`;
  const locationPart = pin
    ? `starting within ${radius} of this location (${pin.lat.toFixed(4)}, ${pin.lng.toFixed(4)})`
    : `starting within ${radius} of this location`;
  return `${vehiclePart} ${vibePart}, ${locationPart}.`;
}

function PillSelector({ options, selected, onSelect, colorMap }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
      {options.map((opt) => {
        const color = colorMap[opt] || parrotBlue;
        const isSelected = selected === opt;
        return (
          <button
            key={opt}
            onClick={() => onSelect(opt)}
            style={{
              padding: "0.3rem 0.75rem",
              borderRadius: "999rem",
              border: `0.09rem solid ${isSelected ? color : parrotPlaceholderGrey}`,
              backgroundColor: isSelected ? color : `${color}15`,
              color: isSelected ? "white" : "#444",
              fontWeight: 600,
              fontSize: "0.85rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function SectionCard({ label, children, style }) {
  return (
    <div style={{
      backgroundColor: "white", borderRadius: "0.875rem", padding: ".75rem 1rem",
      marginBottom: "0.75rem",
      boxShadow: "0 0.125rem 0.5rem rgba(0,0,0,0.06)",
      ...style,
    }}>
      <div style={{
        fontSize: "0.7rem", fontWeight: 800, color: parrotPlaceholderGrey,
        letterSpacing: "0.08em", marginBottom: "0.6rem",
      }}>{label}</div>
      {children}
    </div>
  );
}

function QueryPreview({ vehicle, duration, vibe, radius, pin }) {
  if (!vehicle || !duration || !vibe || !radius || !pin) return null;
  const isOnFoot = ON_FOOT.includes(vehicle);
  const displayDuration = formatDuration(duration);
  const vibeDesc = vibe !== "Any" ? VIBE_DESCRIPTIONS[vibe] : null;
  const vibeKeyword = vibeDesc ? vibeDesc.split(" (")[0] : null;
  const vibeParenthesis = vibeDesc ? " (" + vibeDesc.split(" (")[1] : null;
  const vc = VEHICLE_COLORS[vehicle] || parrotBlue;
  const dc = DURATION_COLORS[duration] || parrotBlue;
  const vibeC = VIBE_COLORS[vibe] || parrotBlue;
  const rc = RADIUS_COLORS[radius] || parrotBlue;

  return (
    <p style={{ fontSize: "0.9rem", lineHeight: 1.6, margin: 0, color: "#333" }}>
      {isOnFoot ? "I want to go for a " : "I have a "}
      <span style={{ color: vc, fontWeight: 700 }}>{vehicle}</span>
      {isOnFoot ? " for " : " and "}
      <span style={{ color: dc, fontWeight: 700 }}>{displayDuration}</span>
      {isOnFoot ? ". " : " available. "}
      {vibe === "Any" ? "I'm looking for a voyage of " : "I'm looking for a "}
      {vibe === "Any"
        ? <span style={{ color: vibeC, fontWeight: 700 }}>any vibe</span>
        : <span style={{ color: vibeC, fontWeight: 700 }}>{vibeKeyword}</span>}
      {vibe !== "Any" && vibeParenthesis && <span style={{ color: "#888" }}>{vibeParenthesis}</span>}
      {vibe === "Any" ? ", starting within " : " experience, starting within "}
      <span style={{ color: rc, fontWeight: 700 }}>{radius}</span>
      {" of this location "}
      {pin && <span style={{ color: parrotBoatPurple, fontWeight: 600 }}>({pin.lat.toFixed(4)}, {pin.lng.toFixed(4)})</span>}
      {"."}
    </p>
  );
}

export default function AskParrotsPage() {
  const [vehicle, setVehicle] = useState(null);
  const [duration, setDuration] = useState(null);
  const [vibe, setVibe] = useState(null);
  const [radius, setRadius] = useState(null);
  const [pin, setPin] = useState(null);
  const [response, setResponse] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sent, setSent] = useState(false);

  const currentUserId = useSelector((state) => state.users.userId);
  const [mapCenter, setMapCenter] = useState([52.2053, 0.1218]); // Cambridge UK fallback
  const [userLocation, setUserLocation] = useState(null);
  const [askParrots, { isLoading }] = useAskParrotsMutation();

  React.useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => {
        const loc = [pos.coords.latitude, pos.coords.longitude];
        setMapCenter(loc);
        setUserLocation(loc);
      },
      () => { }
    );
  }, []);

  const handleMapClick = useCallback((latlng) => setPin(latlng), []);

  const handleAsk = async () => {
    if (!pin) return;
    setResponse(null);
    const radiusNum = parseInt(radius.replace("km", ""), 10);
    try {
      const result = await askParrots({
        vehicleType: vehicle,
        duration: duration === "Half day" ? "Half a Day" : duration,
        vibe,
        radiusKm: radiusNum.toString(),
        latitude: pin.lat,
        longitude: pin.lng,
      }).unwrap();
      setResponse(result.response);
    } catch {
      setResponse("Something went wrong. Please try again.");
    }
  };

  const handleSendMe = async () => {
    if (!response) return;
    const query = buildQueryText(vehicle, duration, vibe, radius, pin);
    const clean = response.replace(/\*\*([^*]+)\*\*/g, "$1");
    const text = `🦜 ${query}\n\n➡️ ${clean}`;
    await invokeHub("SendMessage", currentUserId, currentUserId, text, true);
    setSent(true);
    setTimeout(() => setSent(false), 2000);
  };

  const handleCopy = () => {
    if (!response) return;
    const query = buildQueryText(vehicle, duration, vibe, radius, pin);
    const clean = response.replace(/\*\*([^*]+)\*\*/g, "$1");
    navigator.clipboard.writeText(`${query}\n\n${clean}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderResponse = (text) => {
    return text.split(/\*\*([^*]+)\*\*/).map((part, i) =>
      i % 2 === 1
        ? <span key={i} style={{ color: parrotBlue, fontWeight: 700 }}>{part}</span>
        : <span key={i}>{part}</span>
    );
  };

  const canAsk = !!vehicle && !!duration && !!vibe && !!radius && !!pin;

  return (
    <div className="App">
      <header className="App-header">
        <div className="flex mainpage_Container">
          <div className="flex mainpage_TopRow">
            <TopLeftComponent />
            <div className="flex mainpage_TopRight">
              <TopBarMenu />
            </div>
          </div>

          <div style={{ display: "flex", gap: "1.25rem", padding: "1.25rem", flex: 1, width: "90%", margin: "auto" }}>

            {/* Left panel — 2 parts */}
            <div style={{ flex: 2, overflowY: "auto" }}>
              <div style={{
                backgroundColor: "white", borderRadius: "0.875rem", padding: "0.75rem 1rem",
                display: "flex", alignItems: "center", gap: "0.75rem",
                boxShadow: "0 0.125rem 0.5rem rgba(0,0,0,0.06)", marginBottom: "0.75rem",
              }}>
                <span style={{ fontSize: "1.1rem", fontWeight: 800, color: "#003366" }}>Ask Parrots</span>
                <span style={{ fontSize: "0.85rem", color: parrotPlaceholderGrey }}>
                  Tell me what kind of voyage you're after.
                </span>
              </div>
              <SectionCard label="I WANT TO TRAVEL BY...">
                <PillSelector options={VEHICLES} selected={vehicle} onSelect={setVehicle} colorMap={VEHICLE_COLORS} />
              </SectionCard>
              <SectionCard label="FOR...">
                <PillSelector options={DURATIONS} selected={duration} onSelect={setDuration} colorMap={DURATION_COLORS} />
              </SectionCard>
              <SectionCard label="WITH A VIBE OF...">
                <PillSelector options={VIBES} selected={vibe} onSelect={setVibe} colorMap={VIBE_COLORS} />
              </SectionCard>
              <SectionCard label="STARTING WITHIN...">
                <PillSelector options={RADII} selected={radius} onSelect={setRadius} colorMap={RADIUS_COLORS} />
              </SectionCard>
              <SectionCard label="YOUR QUERY" style={{ minHeight: "8rem" }}>
                <QueryPreview vehicle={vehicle} duration={duration} vibe={vibe} radius={radius} pin={pin} />
              </SectionCard>
              <button
                onClick={handleAsk}
                disabled={!canAsk || isLoading}
                style={{
                  width: "100%", padding: "0.75rem",
                  backgroundColor: canAsk && !isLoading ? parrotWalkTurquoise : "#ccc",
                  color: "white", fontWeight: 800, fontSize: "1rem",
                  border: "none", borderRadius: "999rem", cursor: canAsk && !isLoading ? "pointer" : "not-allowed",
                  boxShadow: canAsk ? "0 0.25rem 0.625rem rgba(0,0,0,0.15)" : "none",
                  transition: "all 0.2s ease",
                }}
              >
                {isLoading ? "Asking Parrots..." : "Ask Parrots"}
              </button>
            </div>

            {/* Right panel — 4 parts */}
            <div style={{ flex: 4, display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              <SectionCard label="AROUND... (CLICK TO SET LOCATION)" style={{ padding: 0, paddingTop: "0.75rem", overflow: "hidden" }}>
                <MapContainer
                  center={mapCenter}
                  zoom={11}
                  style={{ height: "25rem", width: "100%" }}
                  scrollWheelZoom
                >
                  <TileLayer
                    url={`https://api.maptiler.com/maps/outdoor-v4/{z}/{x}/{y}.png?key=${process.env.REACT_APP_MAPTILER_KEY}`}
                    attribution='<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>'
                  />
                  <MapCenterSetter center={mapCenter} />
                  <MapClickHandler onMapClick={handleMapClick} />
                  {userLocation && (
                    <CircleMarker
                      center={userLocation}
                      radius={5}
                      pathOptions={{ color: "#1a73e8", fillColor: "#1a73e8", fillOpacity: 1, weight: 2 }}
                    />
                  )}
                  {pin && <Marker position={pin} icon={purpleIcon} />}
                </MapContainer>
              </SectionCard>

              <SectionCard label="" style={{ minHeight: "13rem" }}>
                {response ? (
                  <>
                    <p style={{
                      fontSize: "1.1rem", lineHeight: 1.6, color: "#333",
                      whiteSpace: "pre-line", margin: 0, textAlign: "left", paddingLeft: "3rem"
                    }}>
                      {renderResponse(response)}
                    </p>
                    <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.25rem", justifyContent: "center" }}>
                      <button onClick={handleCopy} style={btnStyle(parrotBlue)}>
                        {copied ? "Copied!" : "Copy"}
                      </button>
                      <button onClick={handleSendMe} style={btnStyle("#089ADE")}>
                        {sent ? "Sent!" : "Send Me"}
                      </button>
                      <button onClick={() => setResponse(null)} style={btnStyle(parrotWalkTurquoise)}>
                        Close
                      </button>
                    </div>
                  </>
                ) : isLoading ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "1rem 0" }}>
                    <PulsatingParrotLogo size={128} />
                    <span style={{ color: parrotPlaceholderGrey, fontSize: "0.85rem", fontWeight: 600, marginTop: "0.5rem" }}>Thinking...</span>
                  </div>
                ) : (
                  <p style={{ color: parrotPlaceholderGrey, fontSize: "0.9rem", textAlign: "center", marginTop: "2rem" }}>
                  </p>
                )}
              </SectionCard>
            </div>

          </div>
        </div>
      </header>
    </div>
  );
}

const btnStyle = (bg) => ({
  padding: "0.6rem 1.6rem",
  backgroundColor: bg,
  color: "white",
  fontWeight: 700,
  fontSize: "0.95rem",
  border: "none",
  borderRadius: "999rem",
  cursor: "pointer",
  boxShadow: "0 0.125rem 0.375rem rgba(0,0,0,0.15)",
});
